import React from "react";

type RichTextNode = {
  nodeType: string;
  value?: string;
  marks?: Array<{ type: string }>;
  data?: any;
  content?: RichTextNode[];
};

interface RenderOptions {
  paragraphClassName?: string;
}

function renderText(node: RichTextNode, index: number) {
  const text = node.value || "";
  const marks = Array.isArray(node.marks) ? node.marks : [];
  const isBold = marks.some(m => m.type === "bold");
  const isItalic = marks.some(m => m.type === "italic");
  const isUnderline = marks.some(m => m.type === "underline");

  let el: React.ReactNode = text;
  if (isBold) el = <strong>{el}</strong>;
  if (isItalic) el = <em>{el}</em>;
  if (isUnderline) el = <u>{el}</u>;

  return <React.Fragment key={index}>{el}</React.Fragment>;
}

function renderNode(node: RichTextNode, index: number, options?: RenderOptions): React.ReactNode {
  switch (node.nodeType) {
    case "text":
      return renderText(node, index);
    case "paragraph":
      return (
        <p key={index} className={options?.paragraphClassName}>
          {(node.content || []).map((c, i) => renderNode(c, i, options))}
        </p>
      );
    case "heading-1":
    case "heading-2":
    case "heading-3":
    case "heading-4":
    case "heading-5":
    case "heading-6": {
      const level = parseInt(node.nodeType.split("-")[1] || "2", 10);
      const children = (node.content || []).map((c, i) => renderNode(c, i, options));
      const Tag = `h${Math.min(Math.max(level, 1), 6)}` as unknown as keyof JSX.IntrinsicElements;
      return (
        <Tag key={index} className="font-semibold text-foreground">
          {children}
        </Tag>
      );
    }
    case "unordered-list":
      return (
        <ul key={index} className="list-disc list-inside space-y-1">
          {(node.content || []).map((c, i) => renderNode(c, i, options))}
        </ul>
      );
    case "ordered-list":
      return (
        <ol key={index} className="list-decimal list-inside space-y-1">
          {(node.content || []).map((c, i) => renderNode(c, i, options))}
        </ol>
      );
    case "list-item":
      return <li key={index}>{(node.content || []).map((c, i) => renderNode(c, i, options))}</li>;
    case "hyperlink":
      return (
        <a
          key={index}
          href={(node.data && (node.data.uri as string)) || "#"}
          className="underline text-primary-text"
          target="_blank"
          rel="noreferrer"
        >
          {(node.content || []).map((c, i) => renderNode(c, i, options))}
        </a>
      );
    default:
      return (node.content || []).map((c, i) => renderNode(c, i, options));
  }
}

export function renderContentfulRichText(
  doc: { nodeType: string; content?: RichTextNode[] } | null | undefined,
  options?: RenderOptions
): React.ReactNode {
  if (!doc || typeof doc !== "object" || !Array.isArray(doc.content)) return null;
  return doc.content.map((node, index) => renderNode(node, index, options));
}
